import React from 'react';
import { computePosition } from '../../../components/tooltip/positioning/computePosition';
import { arrow, flip, shift } from '../../../components/tooltip/positioning/types';
import { STYLES_NAME } from '../../../constants/stylesName/stylesName';
import { useClickOutside } from '../../../hooks/useClickOutside/useClickOutside';
import { useEscPressedV2 } from '../../../hooks/useEscPressedV2/useEscPressedV2';
import { useActiveBreakpoints } from '../../../hooks/useMediaDevice/useActiveBreakpoints';
import { useStyles } from '../../../hooks/useStyles/useStyles';
import { DeviceBreakpointsType } from '../../../types/breakpoints/breakpoints';
import { focusElementOrFirstDescendant } from '../../../utils/focusHandlers/focusHandlers';
import { TooltipAlignType } from '../types/tooltipAlign';
/**
 * @name useTooltip
 * @description
 * Hook to handle the tooltip functionality
 * @param {UseTooltipType} props
 * @returns {UseTooltipReturnType}
 */
export const useTooltip = ({ align = TooltipAlignType.TOP, ...props }) => {
    const { device: mediaDevice, isDesktop: mediaDeviceIsDesktop } = useActiveBreakpoints();
    const lastFocus = React.useRef(null);
    // Avoid tooltip being opened automatically after closing the tooltip
    const allowFocusOpenTooltip = React.useRef(true);
    const openRef = React.useRef(false);
    const [open, setOpen] = React.useState(false);
    const [placement, setPlacement] = React.useState(align);
    // Need the styles to calculate the arrow tooltip position
    const styles = useStyles(STYLES_NAME.TOOLTIP, props.variant, props.ctv);
    // Avoid showing tooltip when scrolling
    React.useEffect(() => {
        const handleResize = () => {
            updateTooltipPosition();
        };
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const onScroll = (e) => {
        if (!props.tooltipRef.current?.contains(e.target)) {
            updateTooltipPosition();
        }
    };
    const showTooltip = () => {
        if (openRef.current) {
            return;
        }
        openRef.current = true;
        // Save last focusable element
        lastFocus.current = document.activeElement;
        if (mediaDeviceIsDesktop) {
            if (!props.tooltipRef.current) {
                return;
            }
            props.tooltipRef.current.style.display = 'flex';
            updateTooltipPosition();
            focusElementOrFirstDescendant(props.tooltipRef.current);
        }
        setOpen(true);
        props.onOpenClose?.(true);
    };
    const hideTooltip = React.useCallback(() => {
        if (!openRef.current) {
            return;
        }
        // Apply focus to last focusable element
        openRef.current = false;
        allowFocusOpenTooltip.current = false;
        // Only focus on the last element if the tooltip is a modal
        if (props.tooltipAsModal) {
            lastFocus.current?.focus();
        }
        allowFocusOpenTooltip.current = true;
        if (mediaDeviceIsDesktop) {
            if (!props.tooltipRef.current) {
                return;
            }
            props.tooltipRef.current.style.display = 'none';
        }
        setOpen(false);
        props.onOpenClose?.(false);
    }, [mediaDevice, props.onOpenClose, props.tooltipAsModal]);
    const handleEscPress = React.useCallback((event) => {
        if (!openRef.current) {
            return;
        }
        // Only stop propagation if the tooltip is opened and is going to be closed
        event.stopPropagation();
        hideTooltip();
    }, [hideTooltip]);
    // Because desktop does not use popover component, we need to manually call the close on escape and click outside functionality
    // If this is executed, the useEscPressedV2 below this one will not be executed because this would be deeper in the DOM and
    // has the stopPropagation
    useEscPressedV2({
        ref: props.labelRef,
        onEscPress: handleEscPress,
        disableStopPropagation: true,
    });
    // The tooltip should close even if the Escape key is not pressed while focused on the labelRef.
    // The useEscPressed function is necessary for the labelRef to stop event propagation when the Escape key is pressed.
    // This ensures that the event does not unintentionally close a parent element, such as a modal.
    useEscPressedV2({
        onEscPress: handleEscPress,
        disableStopPropagation: true,
    });
    // Prevent closing when clicking the label, it will be handled by the tooltip label click in order to open/close the tooltip
    useClickOutside(props.tooltipRef, hideTooltip, [props.labelRef.current]);
    // This function calculates the position where the tooltip should be displayed
    // eslint-disable-next-line complexity
    const updateTooltipPosition = async () => {
        if (!props.labelRef.current || !props.tooltipRef.current) {
            return;
        }
        const arrowElement = props.tooltipRef.current.lastElementChild;
        try {
            // Use floating-ui native middlewares for proper positioning
            // This approach avoids relying on internal style calculations
            const position = await computePosition(props.labelRef.current, props.tooltipRef.current, {
                placement: align,
                strategy: 'absolute',
                middleware: [
                    // Offset is handle internally with a padding
                    // Avoiding onMouseLeave label close the tooltip when you want to put the mouse inside
                    // Flip placement if there's not enough space
                    flip(),
                    // Shift the tooltip if it would overflow the viewport
                    // This prevents tooltips from being cut off at screen edges
                    shift({ padding: 4 }),
                    // Position the arrow correctly
                    arrow({ element: arrowElement }),
                ],
            });
            const { x, y, placement, middlewareData } = position;
            setPlacement(placement);
            // Apply the position calculated by floating-ui directly
            // The shift middleware will have already handled keeping the tooltip in the viewport
            Object.assign(props.tooltipRef.current.style, {
                left: `${x}px`,
                top: `${y}px`,
            });
            // Arrow position is calculated correctly by the arrow middleware
            // We can use these values directly
            const { y: arrowY, x: arrowX } = middlewareData.arrow;
            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right',
            }[placement.split('-')[0]];
            const arrowStyle = {
                left: arrowX !== null ? `${arrowX}px` : '',
                top: arrowY !== null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: `calc(${styles.tooltipExternalContainer?.[DeviceBreakpointsType.DESKTOP]?.padding ?? 0} - ${styles.arrowContainer?.arrow_position ?? '0px'} / 2)`,
            };
            if (arrowElement && arrowElement.style) {
                arrowElement.style.removeProperty('left');
                arrowElement.style.removeProperty('top');
                arrowElement.style.removeProperty('right');
                arrowElement.style.removeProperty('bottom');
                Object.assign(arrowElement?.style, arrowStyle);
            }
        }
        catch (error) {
            console.error('Error computing position:', error);
        }
    };
    return { showTooltip, hideTooltip, allowFocusOpenTooltip, open, placement };
};
//# sourceMappingURL=useTooltip.js.map